// ***************************************************************************************
// The board, in Three.js.
//
// An ORTHOGRAPHIC camera looking straight on. The tiles have a tactile edge baked into
// their faces, but the board is not tilted into 3D - it is a flat playfield that happens
// to be rendered with a GPU, which is what lets the collapse animate smoothly without
// reflowing the page around it.
//
// TWO RULES THIS FILE OBEYS.
//
// 1. Rendering never decides anything. The caller applies the move to the logical state
//    first and then tells this class what happened. Nothing here can change a score, and
//    no animation callback applies game logic.
//
// 2. The board footprint is FIXED. A 5x5 stays a 5x5 as tiles disappear, so the controls
//    around it never jump.
//
// Tile IDENTITY is tracked separately from the letters, because that is what makes the
// collapse animate: the engine's settle moves letters within an array, so to know which
// tile moved where the same settle is run over an array of ids.
// ***************************************************************************************

import * as THREE from 'three';

import { settleStages, type SettleStage } from '../engine/settle.ts';
import { TileFaceLibrary } from './letterTexture.ts';
import { DirectionArrows } from './directionArrow.ts';

export interface BoardPointerEvents {
  onTileDown(x: number, y: number): void;
  onTileEnter(x: number, y: number): void;
  onPointerUp(): void;
  onPointerCancel(): void;
}

interface Tile {
  id: number;
  letter: string;
  mesh: THREE.Mesh;
  /** Where it is being animated from and to, in board coordinates. */
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  removing: boolean;
  /** When this tile was last selected, for the press. 0 when it is not pressing. */
  pressedAt: number;
}

/** Caps the render resolution. Beyond this the gain is invisible and the cost is not. */
const MAX_PIXEL_RATIO = 2;

// ***************************************************************************************
// Animation timings.
//
// These are pitched to be SEEN, not merely to be brief. The collapse is two different
// rules - everything drops, then every row packs to the right - and if the two passes go
// by in a tenth of a second each, what a player perceives is one diagonal jumble and the
// board's behaviour stays a mystery. So the drop and the slide are separately timed and
// separately eased, and each is long enough to read.
// ***************************************************************************************
export const STAGE_MS = {
  confirm: 170,
  remove: 200,
  /** The pause between one pass and the next, so the two movements read apart. */
  betweenStages: 55,
  /** How long one tile takes to turn over during a shuffle. */
  shuffleFlip: 420,
  /** How long the wave takes to cross the board, corner to corner. */
  shuffleStagger: 520,
};

// ***************************************************************************************
// How long the collapse takes.
//
// The settle is not one movement, it is a LOOP: everything drops, every row packs right,
// and if that opened new gaps it all happens again. Boards routinely take five or six
// passes, so a fixed time per pass either makes a short collapse crawl or makes a long
// one take two and a half seconds - which is what happened when this was tuned by picking
// a number that looked right on one board.
//
// So each pass is timed by HOW FAR TILES ACTUALLY MOVE, and then the whole sequence is
// scaled to fit a budget. A four-cell drop gets its moment; a one-cell nudge does not
// hold the game up; and however many passes it takes, the player is playing again inside
// a second.
// ***************************************************************************************
export const COLLAPSE = {
  /** Every pass gets at least this, or it is a jump rather than a movement. */
  minStageMs: 95,
  /** A pass that shifts one cell, before the per-cell part. */
  baseMs: 80,
  perCellMs: 60,
  maxStageMs: 300,
  /** The whole settle, pauses included. Longer than this and the game feels slow. */
  budgetMs: 820,
};

// ***************************************************************************************
// A SELECTED TILE IS NOT BIGGER. It cannot be.
//
// Tiles are one board unit apart and their faces nearly fill that unit, so a tile scaled
// up sits on top of the ones beside it - and a word is a RUN of selected tiles, so they
// overlap each OTHER, which is worse. At 1.14 a single tile looked fine and a line of
// four was a mess.
//
// Selection is therefore marked entirely within the tile's own square, in the face art
// (see letterTexture.ts). The only thing scale is used for is the press below, which
// comes straight back to 1.
// ***************************************************************************************

/**
 * The press.
 *
 * A tile dips and returns when it joins the word: about a tenth of a second, peaking
 * under a twentieth bigger, ending exactly where it started. It confirms the tap landed
 * on the tile the player meant - the thing a static highlight cannot do on a phone, where
 * the finger is covering the tile at the moment it changes.
 */
const PRESS_SCALE = 1.05;
const PRESS_MS = 150;

/** The camera margin, in board units, on every side of the playfield. */
export const BOARD_MARGIN = 0.12;

/**
 * Screen fraction to grid cell. Pure, so it can be tested without a canvas.
 *
 * `u` and `v` are 0..1 across the canvas, v = 0 at the TOP. The result is a grid cell
 * with y = 0 at the BOTTOM, or null when the point falls in the margin.
 *
 * This is the exact inverse of the placement maths below (worldX / worldY). It is a
 * separate, tested function because when it was merely "derived carefully" it was wrong
 * in a way nothing caught: the board rendered perfectly and no tile could be touched,
 * because every accessible-grid test bypassed this path entirely.
 */
export function cellFromFraction(
  u: number,
  v: number,
  w: number,
  h: number,
  margin: number = BOARD_MARGIN,
): { x: number; y: number } | null {
  if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

  const left = -(w / 2 + margin);
  const right = w / 2 + margin;
  const top = h / 2 + margin;
  const bottom = -(h / 2 + margin);

  const worldX = left + u * (right - left);
  const worldY = top - v * (top - bottom);

  // Tiles are CENTRED on their coordinates, so the cell is the nearest integer. Flooring
  // puts the whole board half a tile out and pushes the bottom row off the grid.
  // The `+ 0` normalises -0 to +0. Math.round(-0.4) is -0, which indexes and compares
  // identically but serialises into a save file as -0 and fails an Object.is check.
  const x = Math.round(worldX + (w - 1) / 2) + 0;
  const y = Math.round(worldY + (h - 1) / 2) + 0;

  if (x < 0 || y < 0 || x >= w || y >= h) return null;

  return { x, y };
}

/**
 * How long to give each pass of the settle. See COLLAPSE.
 *
 * Pure and exported so it can be tested without a canvas, which is the only way this gets
 * checked at all: verifying it on screen means watching an animation with a stopwatch,
 * and the one thing that must be true of it - that a board taking six passes does not
 * take three times as long as one taking two - is exactly what the eye is worst at.
 *
 * It walks the tile positions forward as the animation will, because "how far does this
 * pass move things" is only answerable in terms of where the previous pass left them.
 *
 * @param at where each tile id currently sits. NOT MUTATED.
 */
export interface CollapseTiming {
  /** How long each pass gets, in order. */
  readonly stages: readonly number[];
  /** The beat between passes, which is the first thing given up under pressure. */
  readonly pauseMs: number;
}

export function collapseTimings(
  stages: readonly SettleStage[],
  at: ReadonlyMap<number, { x: number; y: number }>,
  w: number,
): CollapseTiming {
  const positions = new Map(at);
  const wanted: number[] = [];

  for (const stage of stages) {
    const layout = stage.board as unknown as (number | null)[];

    let furthest = 0;

    for (let cell = 0; cell < layout.length; cell++) {
      const id = layout[cell];

      if (id == null) continue;

      const was = positions.get(id);

      if (!was) continue;

      const x = cell % w;
      const y = Math.floor(cell / w);

      furthest = Math.max(furthest, Math.abs(x - was.x), Math.abs(y - was.y));

      positions.set(id, { x, y });
    }

    wanted.push(
      Math.min(
        COLLAPSE.maxStageMs,
        Math.max(COLLAPSE.minStageMs, COLLAPSE.baseMs + COLLAPSE.perCellMs * furthest),
      ),
    );
  }

  // Share out the budget if the board asked for more than it has. Scaled rather than
  // truncated, so a long collapse stays proportioned - the big drop is still the part you
  // see - and never below the floor, which is what stops a scaled-down pass from becoming
  // a jump.
  //
  // WHEN THE TWO CANNOT BOTH BE HAD, the floor wins and the budget is missed. Enough
  // passes at the minimum will exceed any budget, and of the two ways to fail - a
  // collapse that runs long, or one whose passes are invisible - the invisible one is
  // the bug this was written to fix. The pause between passes is given up first, being
  // the part that shows nothing.
  const gaps = Math.max(0, stages.length - 1);
  const pauses = gaps * STAGE_MS.betweenStages;
  const total = wanted.reduce((sum, ms) => sum + ms, 0);

  if (total + pauses <= COLLAPSE.budgetMs) {
    return { stages: wanted, pauseMs: STAGE_MS.betweenStages };
  }

  // Everything at the floor, with no pauses, is the shortest this collapse can honestly
  // be. If that is already over budget then the budget cannot be met, and it is the one
  // that gives way.
  const floors = wanted.length * COLLAPSE.minStageMs;

  if (floors >= COLLAPSE.budgetMs) {
    return { stages: wanted.map(() => COLLAPSE.minStageMs), pauseMs: 0 };
  }

  // Spend what is left ABOVE the floors in proportion to how much each pass asked for
  // beyond it. Scaling the durations themselves and then clamping to the floor does not
  // work: every clamp puts time back that the scale had just taken out, and the total
  // lands over budget again.
  const pauseMs = Math.floor(
    Math.min(STAGE_MS.betweenStages, gaps === 0 ? 0 : (COLLAPSE.budgetMs - floors) / gaps / 2),
  );
  const extra = COLLAPSE.budgetMs - pauseMs * gaps - floors;
  const asked = wanted.reduce((sum, ms) => sum + (ms - COLLAPSE.minStageMs), 0);

  if (asked <= 0 || extra <= 0) {
    return { stages: wanted.map(() => COLLAPSE.minStageMs), pauseMs };
  }

  return {
    stages: wanted.map(
      (ms) =>
        COLLAPSE.minStageMs +
        Math.floor(((ms - COLLAPSE.minStageMs) * extra) / asked),
    ),
    pauseMs,
  };
}

export class BoardRenderer {
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.OrthographicCamera;
  private renderer: THREE.WebGLRenderer | null = null;

  private readonly faces: TileFaceLibrary;
  private readonly geometry = new THREE.PlaneGeometry(1, 1);

  private tiles: Tile[] = [];
  /** Tile id per cell, [y * w + x]. Mirrors the logical board's occupancy. */
  private ids: (number | null)[] = [];
  private nextId = 1;

  private selection = new Set<number>();
  private readonly arrows: DirectionArrows;
  /**
   * Whether to stamp the path arrows.
   *
   * Off in Freedom, where the letters of a word need not touch. An arrow between two
   * tiles at opposite corners lands on a third tile that has nothing to do with the
   * word, which says something false about the move being made.
   */
  private showPath = true;

  private animating = false;
  private frame = 0;
  /** True while a frame is already scheduled, so requests coalesce into one. */
  private renderScheduled = false;
  private disposed = false;

  private pointerDown = false;
  private lastCell = -1;

  private contextLost = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly w: number,
    private readonly h: number,
    private readonly events: BoardPointerEvents,
    private readonly reducedMotion: boolean,
  ) {
    const margin = BOARD_MARGIN;

    this.camera = new THREE.OrthographicCamera(
      -(w / 2 + margin),
      w / 2 + margin,
      h / 2 + margin,
      -(h / 2 + margin),
      -100,
      100,
    );

    this.camera.position.z = 10;

    this.faces = new TileFaceLibrary(1);

    // The selection path, drawn the way the C drew it: an arrow stamped on each tile
    // after the first. See directionArrow.ts.
    this.arrows = new DirectionArrows(this.scene, w * h);

    this.attachPointer();
  }

  // ***************************************************************************************
  // Set-up and teardown
  // ***************************************************************************************
  start(): { ok: true } | { ok: false; reason: string } {
    try {
      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: true,
        alpha: true,
        powerPreference: 'low-power',
      });
    } catch (thrown: unknown) {
      return { ok: false, reason: thrown instanceof Error ? thrown.message : 'WebGL failed' };
    }

    if (!this.renderer) return { ok: false, reason: 'WebGL is not available' };

    this.renderer.setClearAlpha(0);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO));

    this.canvas.addEventListener('webglcontextlost', this.onContextLost);
    this.canvas.addEventListener('webglcontextrestored', this.onContextRestored);

    this.resize();
    this.requestRender();

    return { ok: true };
  }

  private readonly onContextLost = (event: Event): void => {
    // Without preventDefault the context is gone for good; with it, the browser may
    // restore it and the board comes back rather than the page going blank.
    event.preventDefault();
    this.contextLost = true;
  };

  private readonly onContextRestored = (): void => {
    this.contextLost = false;
    this.resize();
    this.requestRender();
  };

  dispose(): void {
    this.disposed = true;

    if (this.frame) cancelAnimationFrame(this.frame);

    this.detachPointer();

    this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);

    for (const tile of this.tiles) this.scene.remove(tile.mesh);

    this.tiles = [];

    this.geometry.dispose();
    this.arrows.dispose();
    this.faces.dispose();
    this.renderer?.dispose();
    this.renderer = null;
  }

  resize(): void {
    if (!this.renderer) return;

    const rect = this.canvas.getBoundingClientRect();
    const size = Math.max(1, Math.min(rect.width, rect.height));

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO));
    this.renderer.setSize(size, size, false);

    this.requestRender();
  }

  // ***************************************************************************************
  // Board contents
  // ***************************************************************************************
  /** Replace the whole board. Used on load, on undo and on restart - no animation. */
  setBoard(board: readonly (string | null)[]): void {
    for (const tile of this.tiles) this.scene.remove(tile.mesh);

    this.tiles = [];
    this.ids = new Array<number | null>(this.w * this.h).fill(null);
    this.selection.clear();

    for (let at = 0; at < board.length; at++) {
      const letter = board[at];

      if (letter == null) continue;

      const id = this.nextId++;
      const x = at % this.w;
      const y = Math.floor(at / this.w);

      this.ids[at] = id;
      this.tiles.push(this.makeTile(id, letter, x, y));
    }

    this.arrows.clear();
    this.requestRender();
  }

  private makeTile(id: number, letter: string, x: number, y: number): Tile {
    const mesh = new THREE.Mesh(this.geometry, this.faces.material(letter, 'idle'));

    mesh.position.set(this.worldX(x), this.worldY(y), 0);

    this.scene.add(mesh);

    return {
      id,
      letter,
      mesh,
      fromX: x,
      fromY: y,
      toX: x,
      toY: y,
      removing: false,
      pressedAt: 0,
    };
  }

  private worldX(x: number): number {
    return x - (this.w - 1) / 2;
  }

  private worldY(y: number): number {
    // y = 0 is the FLOOR, and the camera's +y is up, so this is a direct mapping. Getting
    // this backwards would invert every board in the game.
    return y - (this.h - 1) / 2;
  }

  // ***************************************************************************************
  // The press.
  //
  // Its own small loop rather than a stage of `animate`, because selection happens DURING
  // input rather than between moves: it must never lock anything, never be awaited, and
  // must give way immediately when a move animation starts.
  //
  // Every pressing tile returns to exactly 1. Nothing here leaves a tile a different size
  // from its neighbours, which is the rule the board's spacing depends on.
  // ***************************************************************************************
  private pressing = false;

  private startPress(): void {
    if (this.pressing || this.disposed) return;

    if (this.reducedMotion) {
      for (const tile of this.tiles) tile.pressedAt = 0;

      this.requestRender();

      return;
    }

    this.pressing = true;

    const step = (): void => {
      if (this.disposed) {
        this.pressing = false;
        return;
      }

      // A move or a shuffle owns the tiles while it runs. Standing down is the only safe
      // thing to do - two animations writing one scale is how a tile ends up half-sized
      // and staying there.
      if (this.animating) {
        this.pressing = false;
        return;
      }

      const now = performance.now();

      let moving = false;

      for (const tile of this.tiles) {
        if (tile.pressedAt === 0) continue;

        const t = (now - tile.pressedAt) / PRESS_MS;

        if (t >= 1) {
          tile.pressedAt = 0;
          tile.mesh.scale.setScalar(1);
          continue;
        }

        // Out and back in one arc, so the tile is only ever briefly larger.
        tile.mesh.scale.setScalar(1 + (PRESS_SCALE - 1) * Math.sin(Math.PI * t));
        moving = true;
      }

      this.renderOnce();

      if (moving) requestAnimationFrame(step);
      else this.pressing = false;
    };

    requestAnimationFrame(step);
  }

  /** Whether the selection is drawn as a path. See `showPath`. */
  setShowPath(show: boolean): void {
    if (this.showPath === show) return;

    this.showPath = show;

    if (!show) {
      this.arrows.clear();
      this.requestRender();
    }
  }

  setSelection(tiles: readonly { x: number; y: number }[]): void {
    const wanted = new Set<number>();

    for (const tile of tiles) {
      const id = this.ids[tile.y * this.w + tile.x];

      if (id != null) wanted.add(id);
    }

    let changed = wanted.size !== this.selection.size;

    if (!changed) {
      for (const id of wanted) {
        if (!this.selection.has(id)) {
          changed = true;
          break;
        }
      }
    }

    if (!changed) return;

    const previous = this.selection;

    this.selection = wanted;

    const now = performance.now();

    for (const tile of this.tiles) {
      const selected = this.selection.has(tile.id);
      const wasSelected = previous.has(tile.id);

      tile.mesh.material = this.faces.material(tile.letter, selected ? 'selected' : 'idle');

      // Only a tile that has JUST been selected presses. Re-running it for tiles that
      // were already in the word would make the whole word twitch on every letter.
      if (selected && !wasSelected) tile.pressedAt = now;
      else if (!selected) tile.pressedAt = 0;

      // Selected tiles are drawn in front, so the press is never clipped by a neighbour.
      tile.mesh.position.z = selected ? 0.5 : 0;
    }

    this.startPress();

    if (this.showPath) {
      this.arrows.update(
        tiles.map((tile) => ({ x: this.worldX(tile.x), y: this.worldY(tile.y) })),
      );
    }

    this.requestRender();
  }


  // ***************************************************************************************
  // The move animation.
  //
  // Staged, so the player can follow what happened: the word is confirmed, its tiles go,
  // what is left falls, then it compacts sideways. The LOGICAL result is already decided;
  // these stages only show the journey to it.
  // ***************************************************************************************
  async playMove(removed: readonly { x: number; y: number }[]): Promise<void> {
    if (this.disposed) return;

    this.animating = true;

    try {
      await this.wait(this.reducedMotion ? 0 : STAGE_MS.confirm);

      const removedIds = new Set<number>();

      for (const cell of removed) {
        const at = cell.y * this.w + cell.x;
        const id = this.ids[at];

        if (id != null) removedIds.add(id);

        this.ids[at] = null;
      }

      // EACH DEPARTING TILE GETS ITS OWN MATERIAL.
      //
      // Faces are shared by every tile of that letter, so fading the material fades the
      // whole letter: playing a word with a P in it made every other P on the board blink.
      // A private copy costs one material per departing tile for a fifth of a second.
      const fading: THREE.Material[] = [];

      for (const tile of this.tiles) {
        if (!removedIds.has(tile.id)) continue;

        tile.removing = true;

        const own = this.faces.fadeCopy(tile.letter, 'selected');

        tile.mesh.material = own;
        fading.push(own);
      }

      await this.animate(this.reducedMotion ? 0 : STAGE_MS.remove, (t) => {
        const eased = t * t;

        for (const tile of this.tiles) {
          if (!tile.removing) continue;

          // Up and away rather than simply shrinking: the word is being taken, and a tile
          // that grows slightly as it goes reads as claimed rather than deleted.
          tile.mesh.scale.setScalar(1 * (1 - eased) + 1.35 * eased);
          (tile.mesh.material as THREE.MeshBasicMaterial).opacity = 1 - eased;
        }
      });

      for (const tile of this.tiles) {
        if (tile.removing) this.scene.remove(tile.mesh);
      }

      // The copies have done their job. The textures they point at are the shared ones
      // and are left alone.
      for (const material of fading) material.dispose();

      this.tiles = this.tiles.filter((tile) => !tile.removing);

      // Run the engine's own settle over the IDs. Same rules, same order, so the tiles
      // land exactly where the logical board says they are.
      const stages = settleStages(this.ids as unknown as (string | null)[], this.w, this.h);

      const timings = this.stageTimings(stages);

      for (let index = 0; index < stages.length; index++) {
        const stage = stages[index]!;
        const layout = stage.board as unknown as (number | null)[];

        // A beat between the passes. Without it the slide begins on the frame the drop
        // ends and the two movements blur into one.
        if (index > 0 && !this.reducedMotion) await this.wait(timings.pauseMs);

        for (const tile of this.tiles) {
          tile.fromX = tile.toX;
          tile.fromY = tile.toY;
        }

        for (let at = 0; at < layout.length; at++) {
          const id = layout[at];

          if (id == null) continue;

          const tile = this.tiles.find((candidate) => candidate.id === id);

          if (!tile) continue;

          tile.toX = at % this.w;
          tile.toY = Math.floor(at / this.w);
        }

        const falling = stage.kind === 'down';
        const duration = timings.stages[index]!;

        await this.animate(this.reducedMotion ? 0 : duration, (t) => {
          // Gravity accelerates; a sideways pack is a push that runs out of momentum.
          const eased = falling ? t * t : 1 - (1 - t) * (1 - t);

          for (const tile of this.tiles) {
            tile.mesh.position.x = this.worldX(tile.fromX + (tile.toX - tile.fromX) * eased);
            tile.mesh.position.y = this.worldY(tile.fromY + (tile.toY - tile.fromY) * eased);
          }
        });

        this.ids = layout;
      }

      for (const tile of this.tiles) {
        tile.mesh.position.set(this.worldX(tile.toX), this.worldY(tile.toY), 0);
        tile.pressedAt = 0;
        tile.mesh.scale.setScalar(1);
      }

      this.selection.clear();
      this.arrows.clear();
      this.requestRender();
    } finally {
      this.animating = false;
    }
  }

  private stageTimings(stages: readonly SettleStage[]): CollapseTiming {
    const at = new Map<number, { x: number; y: number }>();

    for (const tile of this.tiles) at.set(tile.id, { x: tile.toX, y: tile.toY });

    return collapseTimings(stages, at, this.w);
  }

  // ***************************************************************************************
  // The shuffle animation.
  //
  // A shuffle does not MOVE tiles. It deals the same letters back out to the same cells,
  // so nothing travels anywhere and sliding tiles around would be showing the player
  // something that did not happen. What actually changes is each tile's FACE - so each
  // tile turns over: it narrows to edge-on, swaps its letter at the point where the face
  // is hidden, and opens up again.
  //
  // The turn is staggered diagonally so it reads as a wave crossing the board rather than
  // twenty-five tiles blinking at once, which is hard to follow and gives no sense of the
  // board being dealt again.
  // ***************************************************************************************
  async playShuffle(board: readonly (string | null)[]): Promise<void> {
    if (this.disposed) return;

    // A shuffle leaves every tile where it was. If the occupancy disagrees then this is
    // not the board this renderer is showing, and snapping to it is more honest than
    // animating a change that did not happen.
    for (let at = 0; at < this.w * this.h; at++) {
      if ((board[at] == null) !== (this.ids[at] == null)) {
        this.setBoard(board);
        return;
      }
    }

    // Drop the selection HERE rather than leaving it to the caller's next render, which
    // would arrive mid-flip and reset every tile's scale for a frame.
    this.clearSelectionNow();

    const byId = new Map<number, Tile>();

    for (const tile of this.tiles) byId.set(tile.id, tile);

    const turns: { tile: Tile; letter: string; delay: number }[] = [];
    const span = Math.max(1, this.w + this.h - 2);

    for (let at = 0; at < this.w * this.h; at++) {
      const id = this.ids[at];
      const letter = board[at];

      if (id == null || letter == null) continue;

      const tile = byId.get(id);

      if (!tile) continue;

      const x = at % this.w;
      const y = Math.floor(at / this.w);

      // Top-left first, so the wave runs the way a board is read.
      const step = (x + (this.h - 1 - y)) / span;

      turns.push({ tile, letter, delay: step * STAGE_MS.shuffleStagger });
    }

    if (this.reducedMotion) {
      for (const turn of turns) this.setFace(turn.tile, turn.letter);

      this.requestRender();

      return;
    }

    this.animating = true;

    const total = STAGE_MS.shuffleFlip + STAGE_MS.shuffleStagger;

    try {
      await this.animate(total, (t) => {
        const elapsed = t * total;

        for (const turn of turns) {
          const local = Math.min(
            1,
            Math.max(0, (elapsed - turn.delay) / STAGE_MS.shuffleFlip),
          );

          // |cos| is 1 at both ends and 0 in the middle: full face, edge-on, full face.
          turn.tile.mesh.scale.x = Math.abs(Math.cos(local * Math.PI));

          // A small lift, so the tile turns rather than simply being squeezed.
          turn.tile.mesh.position.y =
            this.worldY(turn.tile.toY) + Math.sin(local * Math.PI) * 0.1;

          // Swapped at the halfway point, where the face is edge-on and the change cannot
          // be seen happening.
          if (local >= 0.5 && turn.tile.letter !== turn.letter) {
            this.setFace(turn.tile, turn.letter);
          }
        }
      });
    } finally {
      // Whatever happened to the animation - finished, cut short by a hidden tab, or
      // abandoned - the board must end up showing the letters it was given.
      for (const turn of turns) {
        this.setFace(turn.tile, turn.letter);

        turn.tile.pressedAt = 0;
        turn.tile.mesh.scale.setScalar(1);
        turn.tile.mesh.position.set(
          this.worldX(turn.tile.toX),
          this.worldY(turn.tile.toY),
          0,
        );
      }

      this.animating = false;
      this.requestRender();
    }
  }

  private setFace(tile: Tile, letter: string): void {
    tile.letter = letter;
    tile.mesh.material = this.faces.material(
      letter,
      this.selection.has(tile.id) ? 'selected' : 'idle',
    );
  }

  /** Clear the selection immediately, without waiting to be told again by the caller. */
  private clearSelectionNow(): void {
    this.arrows.clear();

    if (this.selection.size === 0) return;

    this.selection.clear();

    for (const tile of this.tiles) {
      tile.mesh.material = this.faces.material(tile.letter, 'idle');
      tile.pressedAt = 0;
      tile.mesh.scale.setScalar(1);
      tile.mesh.position.z = 0;
    }
  }

  get isAnimating(): boolean {
    return this.animating;
  }

  private wait(ms: number): Promise<void> {
    if (ms <= 0) return Promise.resolve();

    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private animate(ms: number, step: (t: number) => void): Promise<void> {
    if (ms <= 0 || this.disposed) {
      step(1);
      this.requestRender();
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const started = performance.now();

      let settled = false;

      const finish = (): void => {
        if (settled) return;

        settled = true;
        clearTimeout(safety);
        step(1);
        this.renderOnce();
        resolve();
      };

      // THE SAFETY TIMER IS LOAD-BEARING, NOT BELT AND BRACES.
      //
      // requestAnimationFrame does not fire in a hidden tab. Without this, switching
      // away mid-collapse leaves the animation's promise pending forever, and because
      // input stays locked until it resolves, the game is dead until a reload. A
      // setTimeout still fires when hidden (throttled, but it fires), so it finishes the
      // move and the board lands where the logical state already says it is.
      const safety = setTimeout(finish, ms + 400);

      const tick = (): void => {
        if (this.disposed || settled) {
          finish();
          return;
        }

        const t = Math.min(1, (performance.now() - started) / ms);

        step(t);
        this.renderOnce();

        if (t >= 1) finish();
        else requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
    });
  }

  // ***************************************************************************************
  // Rendering.
  //
  // There is no always-on loop. A static board is rendered once and then nothing runs
  // until something changes, which on a phone is the difference between a warm handset
  // and a cold one.
  // ***************************************************************************************
  private requestRender(): void {
    if (this.renderScheduled) return;

    this.renderScheduled = true;

    requestAnimationFrame(() => {
      this.renderScheduled = false;
      this.renderOnce();
    });
  }

  private renderOnce(): void {
    if (!this.renderer || this.disposed || this.contextLost) return;

    this.renderer.render(this.scene, this.camera);
  }

  // ***************************************************************************************
  // Pointer input.
  //
  // Pointer events cover mouse, touch and pen with one path. The canvas captures the
  // pointer so a drag that leaves the board still ends cleanly, and `touch-action: none`
  // on the canvas stops the page scrolling DURING a board gesture without disabling
  // scrolling or zooming anywhere else.
  // ***************************************************************************************
  private attachPointer(): void {
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerCancel);
    this.canvas.addEventListener('lostpointercapture', this.onPointerCancel);
  }

  private detachPointer(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerCancel);
    this.canvas.removeEventListener('lostpointercapture', this.onPointerCancel);
  }

  /**
   * Client coordinates to a cell, or null if the point is off the board.
   *
   * Derived as the exact inverse of worldX/worldY, which are what PLACE the tiles. An
   * independently worked-out mapping is how you end up with a board that looks right and
   * cannot be touched - which is exactly what happened here before this was rewritten.
   */
  cellAt(clientX: number, clientY: number): { x: number; y: number } | null {
    const rect = this.canvas.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) return null;

    const u = (clientX - rect.left) / rect.width;
    const v = (clientY - rect.top) / rect.height;

    if (!Number.isFinite(u) || !Number.isFinite(v)) return null;

    return cellFromFraction(u, v, this.w, this.h);
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.animating) return;

    const cell = this.cellAt(event.clientX, event.clientY);

    if (!cell) return;

    event.preventDefault();

    this.canvas.setPointerCapture(event.pointerId);

    this.pointerDown = true;
    this.lastCell = cell.y * this.w + cell.x;

    this.events.onTileDown(cell.x, cell.y);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.pointerDown || this.animating) return;

    const cell = this.cellAt(event.clientX, event.clientY);

    if (!cell) return;

    const at = cell.y * this.w + cell.x;

    if (at === this.lastCell) return;

    this.lastCell = at;

    this.events.onTileEnter(cell.x, cell.y);
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (!this.pointerDown) return;

    this.pointerDown = false;
    this.lastCell = -1;

    if (this.canvas.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId);
    }

    this.events.onPointerUp();
  };

  private readonly onPointerCancel = (): void => {
    if (!this.pointerDown) return;

    this.pointerDown = false;
    this.lastCell = -1;

    this.events.onPointerCancel();
  };
}
